import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import ArticleCard from "../components/ArticleCard";
import SelectMark from "../components/SelectMark";
import EventCard from "../components/EventCard";
import SEO from "../components/SEO";
import { getJson, peekEarlyData } from "../utils/earlyData";
import LeadPicture from "../components/LeadPicture";

// Lead image candidates; index.html preloads the identical srcset/sizes as soon as the listing JSON arrives.
const LEAD_WIDTHS = [480, 800, 1200];
const LEAD_SIZES = "(min-width: 1024px) 740px, (min-width: 768px) 60vw, 100vw";
import { breadcrumbJsonLd } from "../components/Breadcrumbs";
import SubmitStoryCTA from "../components/SubmitStoryCTA";

const CategoryPage = () => {
  const { category } = useParams();
  const earlyPath = category === "latest" ? `/articles?limit=500&compact=true` : `/articles?category=${category}&limit=500&compact=true`;
  // Early fetch from index.html already finished → render the listing (and lead image) in the first paint.
  const [articles, setArticles] = useState(() => (category === "events" ? [] : peekEarlyData(earlyPath) || []));
  const preloaded = useRef(articles.length > 0 ? category : null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(() => articles.length === 0);

  useEffect(() => {
    const fetchData = async () => {
      if (preloaded.current === category) {
        preloaded.current = null;
        return;
      }
      setLoading(true);
      try {
        if (category === "events") {
          const eventsRes = await axios.get(`${API}/events`);
          setEvents(eventsRes.data);
        } else if (category === "latest") {
          setArticles(await getJson(`/articles?limit=500&compact=true`));
        } else {
          setArticles(await getJson(`/articles?category=${category}&limit=500&compact=true`));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [category]);

  const getCategoryInfo = () => {
    const info = {
      people: {
        title: "People Who Matter",
        description: "Profiles of influential individuals shaping the future of business, technology, and society."
      },
      companies: {
        title: "Companies That Matter",
        description: "Feature stories on innovative companies, startups, and industry leaders transforming industries."
      },
      brands: {
        title: "Brands That Matter",
        description: "Brand storytelling highlighting impactful brands and marketing innovations."
      },
      ideas: {
        title: "Ideas That Matter",
        description: "Opinion and thought leadership articles on entrepreneurship, innovation, and leadership."
      },
      initiatives: {
        title: "Initiatives That Matter",
        description: "Bold initiatives, purposeful campaigns, and movements driving meaningful change."
      },
      events: {
        title: "Events That Matter",
        description: "Summits, award ceremonies, leadership forums, and conferences under our platform."
      },
      specials: {
        title: "PWM Specials",
        description: "Editor's picks and hand-crafted features spotlighting the stories that matter most."
      },
      latest: {
        title: "Latest Stories",
        description: "Every story on People Who Matter, newest first, across all sections."
      }
    };
    return info[category] || { title: category, description: "" };
  };

  const categoryInfo = getCategoryInfo();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={`category-page-${category}`}>
      <SEO
        title={categoryInfo.title}
        description={categoryInfo.description}
        url={`/category/${category}`}
        noindex={!loading && articles.length === 0 && events.length === 0 ? "follow" : false}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "CollectionPage", "@id": `https://peoplewhomatter.in/category/${category}`, name: categoryInfo.title, description: categoryInfo.description, isPartOf: { "@id": "https://peoplewhomatter.in/#website" } },
            breadcrumbJsonLd([{ label: "Home", to: "/" }, { label: categoryInfo.title }]),
          ],
        }}
      />
      {/* Header */}
      <section className="py-12 md:py-20 bg-neutral-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-950 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-neutral-950 mb-4">
            {categoryInfo.title}
          </h1>
          <p className="text-xl text-neutral-600 max-w-2xl">
            {categoryInfo.description}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Events Grid */}
          {category === "events" && events.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}

          {/* Lead story */}
          {articles.length > 0 && (
            <Link to={`/article/${articles[0].slug}`} className="group grid md:grid-cols-5 gap-6 md:gap-10 mb-14 md:mb-20" data-testid="category-lead">
              <div className="md:col-span-3 overflow-hidden bg-neutral-100 aspect-[3/2]">
                <LeadPicture
                  src={articles[0].image_url}
                  widths={LEAD_WIDTHS}
                  sizes={LEAD_SIZES}
                  width="1200"
                  height="800"
                  alt={articles[0].title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
              <div className="md:col-span-2 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[11px] font-bold tracking-[0.25em] uppercase text-[#7A1921]">Lead story<SelectMark article={articles[0]} /></span>
                  {articles[0].is_sponsored && <span className="sponsored-badge">Sponsored</span>}
                </div>
                <h2 className="font-serif text-2xl md:text-4xl font-black leading-[1.1] tracking-tight text-neutral-950 group-hover:text-[#7A1921] transition-colors mb-4">{articles[0].title}</h2>
                <p className="text-neutral-600 text-base md:text-lg line-clamp-3 mb-5">{articles[0].excerpt}</p>
                <div className="text-sm text-neutral-500"><span className="font-medium text-neutral-800">{articles[0].author}</span>{articles[0].published_at && <> · {new Date(articles[0].published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</>}</div>
              </div>
            </Link>
          )}

          {/* Articles Grid */}
          {articles.length > 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.slice(1).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}

          {/* Empty State */}
          {articles.length === 0 && events.length === 0 && (
            <div className="text-center py-20">
              <p className="text-neutral-500 text-lg">No content available yet.</p>
              <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
                Return to Home
              </Link>
            </div>
          )}
        </div>
      </section>
      {["people", "brands", "companies", "initiatives"].includes(category) && <SubmitStoryCTA variant="listing" />}
    </div>
  );
};

export default CategoryPage;
